// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * render.ts — the field, drawn.
 *
 * Canvas 2D because there are dozens of moving creeps, a particle system and a
 * live path preview that repaints under a dragging finger; the HUD and menus
 * stay in the DOM where text is crisp and hit targets are easy to keep at 44px.
 *
 * Two rules run through the whole file:
 *
 *  - EVERY meaningful colour comes from palette.ts. No literals. That is what
 *    lets tests/contrast.test.ts pin them, and it is exactly the discipline that
 *    would have caught a wall painted at 1.14:1 against its board.
 *  - EVERY piece is stroked with `outline` before its fill is trusted. The road
 *    is a bright line and pieces stand on it, so the dark stroke is what
 *    guarantees separation where a bright creep crosses a bright road.
 */

import { PALETTE } from './palette';
import { colOf, flow, nextStep, roadCells, rowOf, type FieldSpec } from './field';
import { CREEPS, Game, TOWERS, type Creep, type TowerKind } from './game';

export interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
  colour: string;
  size: number;
}

export interface DragPreview {
  cell: number;
  kind: TowerKind;
  ok: boolean;
  /** Road length if dropped, versus the road length right now. */
  road: number;
  before: number;
}

const TAU = Math.PI * 2;

export class Renderer {
  readonly canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private particles: Particle[] = [];
  private shake = 0;
  private shakeDecay = 0;
  /** Cell size and origin, recomputed on resize. */
  private cs = 0;
  private ox = 0;
  private oy = 0;
  private reduceMotion = false;

  /** Set by main.ts while a tower is being dragged over the field. */
  preview: DragPreview | null = null;
  /** The tower cell whose range ring is showing. */
  selected: number | null = null;
  /** Scratch flow field for the drag preview, reused across frames. */
  private previewFlow: Uint16Array | null = null;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('canvas 2d unavailable');
    this.ctx = ctx;
    this.reduceMotion =
      typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * Fit the canvas to its box at device resolution.
   *
   * Guarded against a transient 0-size measurement: a rect of 0x0 yields a cell
   * size of 0, every world<->screen conversion becomes NaN or Infinity, and
   * input silently stops landing anywhere. Better to keep the last good layout
   * and retry on the next frame.
   */
  resize(spec: FieldSpec): boolean {
    const rect = this.canvas.getBoundingClientRect();
    if (rect.width < 1 || rect.height < 1) return false;

    const dpr = Math.min(window.devicePixelRatio || 1, 3);
    const w = Math.floor(rect.width);
    const h = Math.floor(rect.height);
    if (this.canvas.width !== w * dpr || this.canvas.height !== h * dpr) {
      this.canvas.width = w * dpr;
      this.canvas.height = h * dpr;
    }
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    this.cs = Math.floor(Math.min(w / spec.cols, h / spec.rows));
    this.ox = Math.floor((w - this.cs * spec.cols) / 2);
    this.oy = Math.floor((h - this.cs * spec.rows) / 2);
    return this.cs > 0;
  }

  /** Cell size in CSS px — main.ts needs it for hit-testing and the ghost. */
  cellSize(): number {
    return this.cs;
  }

  /** Screen position of a cell's centre, in CSS px relative to the canvas. */
  cellCentre(spec: FieldSpec, cell: number): [number, number] {
    return [
      this.ox + (colOf(spec, cell) + 0.5) * this.cs,
      this.oy + (rowOf(spec, cell) + 0.5) * this.cs,
    ];
  }

  /**
   * Which cell a point is over, or -1.
   *
   * Takes CLIENT coordinates and subtracts the bounding rect itself, because
   * `offsetX/offsetY` are relative to whatever the event happened to hit and go
   * wrong under DPR scaling and CSS transforms.
   *
   * ── Why this re-measures instead of trusting the last frame ────────────────
   *
   * The cell size and origin used to be computed ONLY inside `draw()`, which
   * runs from requestAnimationFrame. So until the first frame had painted, `cs`
   * was 0 and this returned -1 for every point — and the input path swallows -1
   * silently, because "you tapped outside the board" is not worth a toast. The
   * result was that every tap in that window did nothing at all, with no error,
   * no toast and a board that looked perfectly normal.
   *
   * That is not a theoretical window. A hidden or heavily throttled tab never
   * fires rAF, so a player returning to a backgrounded game, or tapping in the
   * moment between the board appearing and the first frame, hits it. It also
   * hid from every kind of automated check: it only reproduced when a tap
   * happened WITHOUT an intervening repaint, so any test that screenshotted
   * before interacting quietly fired a frame and passed.
   *
   * Input must therefore never depend on rendering having happened. Measuring
   * here is cheap (one getBoundingClientRect) and makes the geometry correct by
   * construction rather than by frame ordering.
   */
  cellAt(spec: FieldSpec, clientX: number, clientY: number): number {
    // Unconditionally, not just when cs is 0: after a rotation or a URL-bar
    // collapse the cached geometry is stale rather than absent, and a stale
    // origin lands taps on the wrong cell — which is worse than dropping them,
    // because it looks like the game misunderstood you. `resize` is idempotent
    // (it only touches canvas.width when the size actually changed, so it never
    // clears the frame) and costs one getBoundingClientRect per pointer event.
    this.resize(spec);
    if (this.cs <= 0) return -1;
    const rect = this.canvas.getBoundingClientRect();
    const col = Math.floor((clientX - rect.left - this.ox) / this.cs);
    const row = Math.floor((clientY - rect.top - this.oy) / this.cs);
    if (col < 0 || col >= spec.cols || row < 0 || row >= spec.rows) return -1;
    return row * spec.cols + col;
  }

  // ── juice ────────────────────────────────────────────────────────────────

  burst(x: number, y: number, colour: string, count = 10, speed = 60): void {
    if (this.reduceMotion) count = Math.min(count, 3);
    for (let i = 0; i < count; i++) {
      const a = (i / count) * TAU + Math.random() * 0.4;
      const s = speed * (0.4 + Math.random() * 0.8);
      this.particles.push({
        x,
        y,
        vx: Math.cos(a) * s,
        vy: Math.sin(a) * s,
        life: 0.5 + Math.random() * 0.3,
        maxLife: 0.8,
        colour,
        size: 2 + Math.random() * 2,
      });
    }
    // A few hundred particles is fine; a leaked wave plus a big cascade is not.
    if (this.particles.length > 400) this.particles.splice(0, this.particles.length - 400);
  }

  /** Screen shake. Fully suppressed under prefers-reduced-motion. */
  kick(amount: number): void {
    if (this.reduceMotion) return;
    this.shake = Math.max(this.shake, amount);
    this.shakeDecay = Math.max(this.shakeDecay, amount * 3);
  }

  private stepParticles(dt: number): void {
    for (const p of this.particles) {
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.vy += 90 * dt;
      p.vx *= 0.96;
      p.life -= dt;
    }
    if (this.particles.some((p) => p.life <= 0)) {
      this.particles = this.particles.filter((p) => p.life > 0);
    }
    if (this.shake > 0) this.shake = Math.max(0, this.shake - this.shakeDecay * dt);
  }

  // ── the frame ────────────────────────────────────────────────────────────

  draw(game: Game, dt: number, timeMs: number): void {
    if (!this.resize(game.spec)) return;
    this.stepParticles(dt);

    const ctx = this.ctx;
    const spec = game.spec;
    const cs = this.cs;

    ctx.save();
    if (this.shake > 0) {
      ctx.translate((Math.random() - 0.5) * this.shake, (Math.random() - 0.5) * this.shake);
    }

    // Ground behind everything, so the shake never reveals a bare edge.
    ctx.fillStyle = PALETTE.ground;
    ctx.fillRect(-40, -40, this.canvas.width + 80, this.canvas.height + 80);

    // Field.
    ctx.fillStyle = PALETTE.board;
    ctx.fillRect(this.ox, this.oy, cs * spec.cols, cs * spec.rows);

    ctx.strokeStyle = PALETTE.boardGrid;
    ctx.lineWidth = 1;
    ctx.beginPath();
    for (let c = 1; c < spec.cols; c++) {
      ctx.moveTo(this.ox + c * cs + 0.5, this.oy);
      ctx.lineTo(this.ox + c * cs + 0.5, this.oy + cs * spec.rows);
    }
    for (let r = 1; r < spec.rows; r++) {
      ctx.moveTo(this.ox, this.oy + r * cs + 0.5);
      ctx.lineTo(this.ox + cs * spec.cols, this.oy + r * cs + 0.5);
    }
    ctx.stroke();

    this.drawRoadFrom(spec, game.dist, game.road(), PALETTE.road, Math.max(3, cs * 0.16));
    this.drawGatesAndHeart(game, timeMs);
    if (this.preview) this.drawPreview(game);
    this.drawTowers(game);
    this.drawCreeps(game);
    this.drawParticles();

    ctx.restore();
  }

  /**
   * The road, as a rounded polyline through cell centres.
   *
   * `roadCells()` returns a sorted SET, so walking it in index order would draw
   * a line that cuts across the field. The route has to be re-walked through the
   * flow field, one run per Gate, using `nextStep` — the SAME function the
   * creeps walk with, so the line you are shown is by construction the line they
   * take rather than a second implementation that can drift from it.
   */
  private drawRoadFrom(
    spec: FieldSpec,
    dist: Uint16Array,
    cells: number[],
    colour: string,
    width: number,
  ): void {
    if (cells.length < 2) return;
    const ctx = this.ctx;
    const inRoad = new Set(cells);

    ctx.strokeStyle = colour;
    ctx.lineWidth = width;
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';

    for (const gate of spec.gates) {
      const run: number[] = [];
      let cell = gate;
      let guard = 0;
      while (cell >= 0 && inRoad.has(cell) && guard++ <= spec.size) {
        run.push(cell);
        if (cell === spec.heart) break;
        cell = nextStep(spec, dist, cell);
      }
      if (run.length < 2) continue;
      ctx.beginPath();
      run.forEach((c, i) => {
        const [x, y] = this.cellCentre(spec, c);
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      });
      ctx.stroke();
    }
  }

  private drawGatesAndHeart(game: Game, timeMs: number): void {
    const ctx = this.ctx;
    const spec = game.spec;
    const cs = this.cs;

    for (const gate of spec.gates) {
      const [x, y] = this.cellCentre(spec, gate);
      ctx.fillStyle = PALETTE.gate;
      ctx.strokeStyle = PALETTE.outline;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.roundRect(x - cs * 0.34, y - cs * 0.34, cs * 0.68, cs * 0.68, cs * 0.14);
      ctx.fill();
      ctx.stroke();
    }

    // The Heart beats, and it beats FASTER as it weakens — the one piece of
    // feedback that does not need a number to be read.
    const frac = game.heart / game.mode.heartBeats;
    const rate = 900 - 500 * (1 - Math.max(0, frac));
    const pulse = 1 + 0.12 * Math.sin((timeMs / rate) * TAU);
    const [hx, hy] = this.cellCentre(spec, spec.heart);
    const r = cs * 0.4 * (this.reduceMotion ? 1 : pulse);
    ctx.fillStyle = frac <= 0.34 ? PALETTE.heartLow : PALETTE.heart;
    ctx.strokeStyle = PALETTE.outline;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(hx, hy, r, 0, TAU);
    ctx.fill();
    ctx.stroke();
  }

  /**
   * The drag preview: the road the creeps WOULD take, plus a delta badge.
   *
   * This is the signature piece of feel — it turns "where should this go" from a
   * guess into something you can see before you commit.
   */
  private drawPreview(game: Game): void {
    const p = this.preview;
    if (!p) return;
    const ctx = this.ctx;
    const spec = game.spec;
    const cs = this.cs;
    const [x, y] = this.cellCentre(spec, p.cell);

    // The projected road, drawn over the live one. This is the whole point of
    // the preview: you can SEE the corridor you are about to create before you
    // spend on it, rather than dropping a tower and finding out.
    if (p.ok) {
      const saved = game.blocked[p.cell];
      game.blocked[p.cell] = 1;
      flow(spec, game.blocked, this.previewFlow ?? (this.previewFlow = new Uint16Array(spec.size)));
      const projected = roadCells(spec, this.previewFlow);
      game.blocked[p.cell] = saved;

      ctx.save();
      ctx.globalAlpha = 0.55;
      this.drawRoadFrom(spec, this.previewFlow, projected, PALETTE.preview, Math.max(2, cs * 0.1));
      ctx.restore();
    }

    ctx.save();
    ctx.globalAlpha = 0.85;
    ctx.fillStyle = p.ok ? PALETTE.preview : PALETTE.previewBad;
    ctx.strokeStyle = PALETTE.outline;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.roundRect(x - cs * 0.36, y - cs * 0.36, cs * 0.72, cs * 0.72, cs * 0.16);
    ctx.fill();
    ctx.stroke();
    ctx.restore();

    // The delta badge: how much longer or shorter the road becomes.
    if (p.ok) {
      const delta = p.road - p.before;
      const label = delta > 0 ? `+${delta}` : `${delta}`;
      ctx.font = `600 ${Math.max(11, cs * 0.42)}px ui-sans-serif, system-ui, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.lineWidth = 4;
      ctx.strokeStyle = PALETTE.outline;
      ctx.strokeText(label, x, y - cs * 0.85);
      ctx.fillStyle = delta > 0 ? PALETTE.good : delta < 0 ? PALETTE.danger : PALETTE.textDim;
      ctx.fillText(label, x, y - cs * 0.85);
    }
  }

  private drawTowers(game: Game): void {
    const ctx = this.ctx;
    const spec = game.spec;
    const cs = this.cs;

    for (const tower of game.towers.values()) {
      const spec2 = TOWERS[tower.kind];
      const [x, y] = this.cellCentre(spec, tower.cell);
      const colour =
        tower.kind === 'spitter'
          ? PALETTE.towerSpitter
          : tower.kind === 'mire'
            ? PALETTE.towerMire
            : PALETTE.towerLance;

      // Range ring for the selected tower, under the body.
      if (this.selected === tower.cell) {
        ctx.save();
        ctx.globalAlpha = 0.18;
        ctx.fillStyle = colour;
        ctx.beginPath();
        ctx.arc(x, y, spec2.range[tower.level] * cs, 0, TAU);
        ctx.fill();
        ctx.restore();
        ctx.strokeStyle = colour;
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(x, y, spec2.range[tower.level] * cs, 0, TAU);
        ctx.stroke();
      }

      ctx.fillStyle = colour;
      ctx.strokeStyle = PALETTE.outline;
      ctx.lineWidth = 2;
      const r = cs * 0.33;

      // GLYPH, not hue, is what tells the three towers apart — all three sit in
      // the same luminance band because they all have to clear 3:1 on a
      // near-black board. See palette.ts.
      ctx.beginPath();
      if (spec2.glyph === 'disc') {
        ctx.arc(x, y, r, 0, TAU);
      } else if (spec2.glyph === 'ring') {
        ctx.arc(x, y, r, 0, TAU);
        ctx.arc(x, y, r * 0.5, 0, TAU, true);
      } else {
        ctx.moveTo(x, y - r);
        ctx.lineTo(x + r, y + r * 0.75);
        ctx.lineTo(x, y + r * 0.25);
        ctx.lineTo(x - r, y + r * 0.75);
        ctx.closePath();
      }
      ctx.fill('evenodd');
      ctx.stroke();

      // Level pips, engraved dark on the tower's own colour.
      if (tower.level > 0) {
        ctx.fillStyle = PALETTE.towerPip;
        for (let i = 0; i < tower.level; i++) {
          ctx.beginPath();
          ctx.arc(x - cs * 0.1 + i * cs * 0.2, y + cs * 0.42, Math.max(1.5, cs * 0.045), 0, TAU);
          ctx.fill();
        }
      }
    }
  }

  private drawCreeps(game: Game): void {
    const ctx = this.ctx;
    const cs = this.cs;

    for (const c of game.creeps) {
      if (c.done) continue;
      const [gx, gy] = game.creepXY(c);
      const x = this.ox + (gx + 0.5) * cs;
      const y = this.oy + (gy + 0.5) * cs;
      const kind = CREEPS[c.kind];
      const r = kind.radius * cs;

      ctx.fillStyle =
        c.kind === 'crawler'
          ? PALETTE.creepCrawler
          : c.kind === 'runner'
            ? PALETTE.creepRunner
            : c.kind === 'husk'
              ? PALETTE.creepHusk
              : PALETTE.creepSpore;
      ctx.strokeStyle = PALETTE.outline;
      ctx.lineWidth = 2;

      // SHAPE and SIZE are the channel here too — a husk is nearly twice a
      // runner's radius, and the four fills all sit high on the luminance scale
      // because they all have to read against a near-black board.
      ctx.beginPath();
      this.creepPath(x, y, r, c);
      ctx.fill();
      ctx.stroke();

      // Health sliver, only once hurt — an untouched wave stays clean.
      if (c.hp < c.maxHp) {
        const w = r * 2;
        ctx.fillStyle = PALETTE.outline;
        ctx.fillRect(x - w / 2, y - r - 6, w, 3);
        ctx.fillStyle = PALETTE.creepHurt;
        ctx.fillRect(x - w / 2, y - r - 6, w * Math.max(0, c.hp / c.maxHp), 3);
      }
    }
  }

  private creepPath(x: number, y: number, r: number, c: Creep): void {
    const ctx = this.ctx;
    switch (CREEPS[c.kind].shape) {
      case 'disc':
        ctx.arc(x, y, r, 0, TAU);
        break;
      case 'diamond':
        ctx.moveTo(x, y - r);
        ctx.lineTo(x + r, y);
        ctx.lineTo(x, y + r);
        ctx.lineTo(x - r, y);
        ctx.closePath();
        break;
      case 'hex':
        for (let i = 0; i < 6; i++) {
          const a = (i / 6) * TAU - Math.PI / 2;
          const px = x + Math.cos(a) * r;
          const py = y + Math.sin(a) * r;
          if (i === 0) ctx.moveTo(px, py);
          else ctx.lineTo(px, py);
        }
        ctx.closePath();
        break;
      case 'star':
        for (let i = 0; i < 10; i++) {
          const a = (i / 10) * TAU - Math.PI / 2;
          const rad = i % 2 === 0 ? r : r * 0.5;
          const px = x + Math.cos(a) * rad;
          const py = y + Math.sin(a) * rad;
          if (i === 0) ctx.moveTo(px, py);
          else ctx.lineTo(px, py);
        }
        ctx.closePath();
        break;
    }
  }

  private drawParticles(): void {
    const ctx = this.ctx;
    for (const p of this.particles) {
      ctx.save();
      ctx.globalAlpha = Math.max(0, Math.min(1, p.life / p.maxLife));
      ctx.fillStyle = p.colour;
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size, 0, TAU);
      ctx.fill();
      ctx.restore();
    }
  }
}
